/* GNS Reviews - read more, wall "load more", and optional carousel auto-scroll. */

if (!customElements.get('gns-reviews')) {
  class GnsReviews extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        setTimeout(() => this.init(), 0);
      }
    }

    init() {
      this.readMoreLabel = this.dataset.readMore || 'Read more';
      this.readLessLabel = this.dataset.readLess || 'Read less';

      requestAnimationFrame(() => this.setupReadMore());
      this.setupLoadMore();
      this.setupAutoplay();
    }

    /**
     * Adds a "Read more" toggle to any clamped review body that overflows.
     */
    setupReadMore() {
      const bodies = this.querySelectorAll('.review-card__body--clamp');

      bodies.forEach((body) => {
        if (body.scrollHeight - body.clientHeight < 6) return;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'review-card__readmore';
        btn.textContent = this.readMoreLabel;
        btn.setAttribute('aria-expanded', 'false');

        btn.addEventListener('click', () => {
          const expanded = body.classList.toggle('is-expanded');
          btn.textContent = expanded ? this.readLessLabel : this.readMoreLabel;
          btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });

        body.insertAdjacentElement('afterend', btn);
      });
    }

    /**
     * Reveals hidden wall cards when the "show more" button is clicked.
     */
    setupLoadMore() {
      const btn = this.querySelector('[data-load-more]');
      if (!btn) return;

      btn.addEventListener('click', () => {
        this.querySelectorAll('[data-review-hidden]').forEach((el) => {
          el.removeAttribute('data-review-hidden');
        });
        btn.remove();
      });
    }

    /**
     * Optional auto-scroll for the carousel layout.
     */
    setupAutoplay() {
      if (this.dataset.gnsAutoplay !== 'true') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.slider = this.querySelector('.slider');
      const firstItem = this.querySelector('.slider__item');
      if (!this.slider || !firstItem) return;

      const speed = (parseFloat(this.dataset.gnsSpeed) || 5) * 1000;
      this.paused = false;

      const step = () => {
        if (this.paused) return;
        const itemWidth = firstItem.getBoundingClientRect().width
          + parseFloat(getComputedStyle(this.slider.querySelector('.slider__grid')).columnGap || 0);
        const atEnd = this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 4;
        this.slider.scrollTo({
          left: atEnd ? 0 : this.slider.scrollLeft + itemWidth,
          behavior: 'smooth',
        });
      };

      this.autoplayTimer = setInterval(step, speed);

      const pause = () => { this.paused = true; };
      const resume = () => { this.paused = false; };
      this.addEventListener('mouseenter', pause);
      this.addEventListener('mouseleave', resume);
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', resume);

      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => { this.paused = !entry.isIntersecting; });
        }).observe(this);
      }
    }
  }

  customElements.define('gns-reviews', GnsReviews);
}

/* Compact single-review rotator (buy-box / tight spaces). */
if (!customElements.get('gns-review-rotator')) {
  class GnsReviewRotator extends HTMLElement {
    constructor() {
      super();
      if (window.initLazyScript) {
        window.initLazyScript(this, this.init.bind(this));
      } else {
        setTimeout(() => this.init(), 0);
      }
    }

    init() {
      this.items = Array.from(this.querySelectorAll('.gns-rotator__item'));
      this.hover = false;
      this.expanded = false;

      this.setupShowMore();

      if (this.items.length < 2) return;

      this.dots = Array.from(this.querySelectorAll('.gns-rotator__dot'));
      this.index = 0;
      this.speed = (parseFloat(this.dataset.speed) || 4) * 1000;

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { this.show(i); this.restart(); });
      });

      this.addEventListener('mouseenter', () => { this.hover = true; });
      this.addEventListener('mouseleave', () => { this.hover = false; });

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.start();
      }
    }

    /**
     * Adds a "Show more" toggle to any quote that overflows the 3-line clamp.
     */
    setupShowMore() {
      this.querySelectorAll('.gns-rotator__quote').forEach((quote) => {
        requestAnimationFrame(() => {
          if (quote.scrollHeight - quote.clientHeight < 4) return;

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'gns-rotator__more';
          btn.textContent = 'Show more';
          btn.addEventListener('click', () => {
            this.expanded = quote.classList.toggle('is-expanded');
            btn.textContent = this.expanded ? 'Show less' : 'Show more';
          });
          quote.insertAdjacentElement('afterend', btn);
        });
      });
    }

    start() {
      this.timer = setInterval(() => {
        if (!this.hover && !this.expanded) this.show(this.index + 1);
      }, this.speed);
    }

    restart() {
      clearInterval(this.timer);
      this.start();
    }

    show(i) {
      // Collapse any expanded quote when moving to another review.
      this.expanded = false;
      this.querySelectorAll('.gns-rotator__quote.is-expanded').forEach((q) => q.classList.remove('is-expanded'));
      this.querySelectorAll('.gns-rotator__more').forEach((b) => { b.textContent = 'Show more'; });

      this.index = (i + this.items.length) % this.items.length;
      this.items.forEach((el, idx) => el.classList.toggle('is-active', idx === this.index));
      if (this.dots) this.dots.forEach((dot, idx) => dot.setAttribute('aria-current', idx === this.index ? 'true' : 'false'));
    }
  }

  customElements.define('gns-review-rotator', GnsReviewRotator);
}
